import type { BarChartData, SingleValue, SortOption } from '@/core';

/**
 * Convert raw bar values into percentages of the visible total. Returns the
 * visible-bar value sum alongside the rescaled rows so callers (e.g. dynamic
 * threshold rescaling in `BarChartPanel`) can reuse the exact denominator
 * applied to the bars instead of recomputing it.
 */
export function calculatePercentages(data: BarChartData[]): {
  percentageBars: BarChartData[];
  barsValueSum: number;
} {
  let sum = 0;
  let comparisonSum = 0;
  for (const item of data) {
    sum += item.value ?? 0;
    comparisonSum += item.comparisonValue ?? 0;
  }

  const result: BarChartData[] = [];
  for (const item of data) {
    const percentage = sum > 0 ? ((item.value ?? 0) / sum) * 100 : 0;
    const comparisonPercentage =
      item.comparisonValue !== undefined && comparisonSum > 0
        ? ((item.comparisonValue ?? 0) / comparisonSum) * 100
        : item.comparisonValue;

    result.push({
      ...item,
      value: percentage,
      comparisonValue: comparisonPercentage,
    });
  }

  return { percentageBars: result, barsValueSum: sum };
}

/**
 * sortTopListSeriesData mutates the original data. Switching between default and asc/desc sort works here
 * because BarChartPanel generates a new array before calling this function.
 */
export function sortTopListSeriesData(data: BarChartData[], sortOrder: SortOption = 'default') {
  if (sortOrder === 'asc') {
    // Smallest values at the top (nulls sink to bottom)
    return data.sort((a, b) => {
      if (a.value === null) return 1;
      if (b.value === null) return -1;
      return a.value - b.value;
    });
  } else if (sortOrder === 'desc') {
    // Largest values at the top (nulls sink to bottom)
    return data.sort((a, b) => {
      if (a.value === null) return 1;
      if (b.value === null) return -1;
      return b.value - a.value;
    });
  } else if (sortOrder === 'series-name-asc') {
    return data.sort((a, b) => a.label.localeCompare(b.label));
  } else if (sortOrder === 'series-name-desc') {
    return data.sort((a, b) => b.label.localeCompare(a.label));
  }
  return data;
}

/**
 * Links a comparison value to it's corresponding baseline series
 */
export function linkComparisonSeries(barChartData: BarChartData[], currentSeries: SingleValue) {
  if (currentSeries.config?.compareTo !== undefined && currentSeries.config?.compareTo !== null) {
    const baselineSeries = barChartData[currentSeries.config.compareTo];

    if (baselineSeries) {
      barChartData[currentSeries.config.compareTo] = {
        ...baselineSeries,
        comparisonValue: currentSeries.value,
      }; // go back and update baselineSeries to include the comparison value
    }
  }
}
